import React, { useEffect, useState } from "react";
import BarChart from "./BarChart";
import emptyStateBar from "../assets/empty-state-bar.svg";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0, // No decimal places
  maximumFractionDigits: 0, // No decimal places
});

const FundingSummary = ({ capital }) => {
  const [hasData, setHasData] = useState(false);

  const [data, setData] = useState({
    positive: [],
    help: [],
    negative: [],
  });

  useEffect(() => {
    setHasData(
      capital.fundedCapital || capital.totalCapital || capital.unfundedCapital
    );
    const positive = [
      capital.fundedCapital,
      capital.unfundedCapital,
      capital.fundedCapital + capital.unfundedCapital,
    ];
    const help = [0, capital.fundedCapital, 0];

    setData({ positive, help });
  }, [capital]);

  const options = {
    tooltip: {
      trigger: "item",
      axisPointer: {
        type: "shadow",
      },
      formatter: function (params) {
        if (!Array.isArray(params)) {
          params = [params];
        }
        let tooltipContent = "";
        params.forEach((param) => {
          // Customize tooltip content based on series name
          if (param.seriesName === "Funded & Unfunded") {
            // Displaying only non-"-" values and skipping the helper series
            if (param.dataIndex === 0) {
              tooltipContent += `Called: ${currencyFormatter.format(
                capital.fundedCapital
              )}<br/>`;
            } else if (param.dataIndex === 1) {
              tooltipContent += `Outstanding: ${currencyFormatter.format(
                capital.unfundedCapital
              )}<br/>`;
            } else if (param.dataIndex === 2) {
              tooltipContent += `Total Commitment: ${currencyFormatter.format(
                capital.totalCapital
              )}<br/>`;
            }
          }
        });
        return tooltipContent;
      },
    },
    grid: {
      left: "1%",
      right: "4%",
      bottom: "1%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      splitLine: { show: false },
      data: ["Called", "Outstanding", "Total Commitment"],
      axisLabel: {
        formatter: function (value) {
          switch (value) {
            case "Called":
              return `${value} ⓘ`;
            case "Outstanding":
              return `${value} ⓘ`;
            case "Total Commitment":
              return `${value}`;
            default:
              return value;
          }
        },
      },
      axisPointer: {
        show: true,
        type: "none",
        label: {
          show: true,
          formatter: function (params) {
            if (params.value === "Called") {
              return "Amount of total investments that have been called to date.";
            } else if (params.value === "Outstanding") {
              return "Remaining balance of total investments that will be called over the life of the investments.";
            }
          },
        },
      },
    },
    yAxis: {
      show: false,
    },
    series: [
      {
        type: "bar",
        stack: "total",
        itemStyle: {
          color: "rgba(0,0,0,0)", // Transparent for the helper series
        },
        emphasis: {
          itemStyle: {
            color: "rgba(0,0,0,0)", // Keep it transparent on hover
          },
        },
        data: data.help,
        animation: true,
        animationDuration: 1500,
        animationEasing: "exponentialInOut",
      },
      {
        name: "Funded & Unfunded",
        type: "bar",
        stack: "total",
        // color: "#1839d4",
        itemStyle: {
          color: function (params) {
            // Change color dynamically based on the dataIndex
            if (params.dataIndex === 2) {
              return "#001f60"; // A distinct color for Total Commitment
            }
            return "#1839d4"; // Default color for other bars
          },
        },
        label: {
          show: true,
          formatter: function (params) {
            if (!params.value) return "";
            return currencyFormatter.format(params.value);
          },
          position: "top",
        },
        data: data.positive,
        barWidth: 30,
        animation: true,
        animationDuration: 1500, // Duration in milliseconds
        animationEasing: "exponentialInOut", // Easing function
      },
    ],
  };

  return (
    <div className="h-full flex flex-col">
      <h2
        className="bg-vennre-blue text-sm md:text-lg text-white font-semibold px-6 py-2 sm:rounded-t-lg"
        data-section-title
      >
        Funding Summary
      </h2>
      <div className="bg-white p-6 shadow-md rounded-lg grow">
        <div className="flex justify-center items-center rounded h-full">
          {hasData ? (
            <BarChart options={options} />
          ) : (
            <div>
              <img
                src={emptyStateBar}
                alt="No Data"
                className="h-40 w-40 mx-auto"
              />
              <p className="mt-4 text-slate-500 text-center">
                Unlock your private wealth journey.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FundingSummary;
